import React, { useState } from 'react';
import { TextField, Button, Grid, Typography, Container, Box, InputAdornment, IconButton, Link, CircularProgress } from '@mui/material';
import { handleSpaces, getErrorMessage } from '../utils/helper';
import axios from 'axios';
import { success, danger } from '../utils/message-box';
import { useLocation, useNavigate } from "react-router-dom";
import VipFormerLogo from '../assets/images/logo.png';
import axiosInstance from '../Services/axiosInstance.js';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const SignUp = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateEmail = (email) => {
        return emailRegex.test(email);
    };

    const decodeJWT = (token) => {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
      
          return JSON.parse(jsonPayload);
        } catch (error) {
          console.error("Invalid token", error);
          return null;
        }
      };

    const splitName = (fullName) => {
        const trimmedName = fullName.trim();
        if (!trimmedName) {
            return { first_name: '', last_name: '' };
        }
        const parts = trimmedName.split(/\s+/);
        const first_name = parts[0] || '';
        const last_name = parts.slice(1).join(' ') || '';
        return { first_name, last_name };
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (loading) {
            return false;
        }
        
        if (!username || !email || !password) {
            setError('Please fill in all fields.');
            danger('Please fill in all fields.');
            return false;
        }

        if (usernameError || emailError || passwordError) {
            return false;
        }

        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            danger('Please enter a valid email address.');
            return false;
        }
        
        setError('');
        setLoading(true);
        const { first_name, last_name } = splitName(username);
        axiosInstance.post('/api/v1/auth/signup', { 
            email, 
            password,
            first_name: first_name,
            last_name: last_name,
        })
        .then((res) => {
            setError('');
            // localStorage.setItem('access-token', res.data.accessToken);
            // localStorage.setItem('refresh-token', res.data.refreshToken);
            // const decodedToken = decodeJWT(res.data.accessToken);
            // localStorage.setItem('email', decodedToken.email);
            success('Account created successfully!');
            setTimeout(() => {
                navigate('/signin');
            }, 100);
        }).catch((error) => {
            const msg = getErrorMessage(error, 'Sign up failed. Please try again.');
            danger(msg);
            setError('Sign up failed. Please try again.');
        }).finally(() => {
            setLoading(false);
        });
        
        return false;
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };
    
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <>
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Grid className='ml-40 text-center'>
                    <Typography>
                        <img src={VipFormerLogo} style={{ width: "50%" }}></img>
                    </Typography>
                    <Typography className="gray7 f-16 font-weight-400 mt-20">
                        Please fill your details to create an account
                    </Typography>
                </Grid>
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} className='login-padding'>
                            <Typography className="gray8 font-weight-500 f-14">Username</Typography>
                            <TextField
                                required
                                type="text"
                                className={`form-control input-group mb-0 inputcontrasts${usernameError ? "is-invalid" : ""
                                    }`}
                                margin="normal"
                                placeholder="Enter username"
                                onKeyPress={handleSpaces}
                                value={username}
                                onChange={(e) => {
                                    setUsername(e.target.value);
                                    if (e.target.value?.length > 128) {
                                        setUsernameError('Exceeded maximum character length of 128');
                                    } else {
                                        setUsernameError('');
                                    }
                                }}
                            />
                            <span className="danger-color error-msg">
                                {" "}
                                {usernameError}
                            </span>
                        </Grid>
                        <Grid item xs={12} className='login-padding'>
                            <Typography className="gray8 font-weight-500 f-14">Email</Typography>
                            <TextField
                                required
                                type="email"
                                className={`form-control input-group mb-0 inputcontrasts${emailError ? "is-invalid" : ""
                                    }`}
                                margin="normal"
                                placeholder="Enter email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (e.target.value?.length > 128) {
                                        setEmailError('Exceeded maximum character length of 128');
                                    } else if (e.target.value && !validateEmail(e.target.value)) {
                                        setEmailError('Please enter a valid email address.');
                                    } else {
                                        setEmailError('');
                                    }
                                }}
                            />
                            <span className="danger-color error-msg">
                                {" "}
                                {emailError}
                            </span>
                        </Grid>
                        <Grid item xs={12} className='login-padding'>
                            <Typography className="gray8 font-weight-500 f-14">Password</Typography>
                            <TextField
                                required
                                type={showPassword ? "text" : "password"}
                                className={`form-control sign-field input-group mb-0 inputcontrasts${passwordError ? "is-invalid" : ""
                                    }`}
                                margin="normal"
                                placeholder="Enter password"
                                onKeyPress={handleSpaces}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (e.target.value?.length > 128) {
                                        setPasswordError('Exceeded maximum character length of 128');
                                    } else {
                                        setPasswordError('');
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                        <IconButton
                                            aria-label="show-hide-password"
                                            onClick={handleClickShowPassword}
                                            onKeyPress={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            sx={{
                                                "& .MuiTouchRipple-root": { position: "static" },
                                            }}
                                            tabIndex={0}
                                        >
                                            {showPassword ? (
                                                <Visibility
                                                    aria-label="Visibility icon"
                                                    tabIndex={-1}
                                                />
                                            ) : (
                                                <VisibilityOffIcon
                                                    aria-label="Visibilityoff icon"
                                                    tabIndex={-1}
                                                />
                                            )}
                                        </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <span className="danger-color error-msg">
                                {" "}
                                {passwordError}
                            </span>
                        </Grid>
                    </Grid>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        className='btn primary-button login-button'
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {loading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                    <Grid container justifyContent="center">
                        <Grid item>
                            <Typography className="gray7 f-14 font-weight-400 text-center mt-10">
                                Already have an account?{' '}
                                <Link 
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate('/signin')}
                                    className="link-color f-14 font-weight-500"
                                    sx={{ textDecoration: 'none' }}
                                >
                                    Sign In
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Container>
        </>
    );
};

export default SignUp;

